/**
 * Laboratory API client.
 *
 * Direct implementation of database operations via REST API.
 * Replaces dependency on cylian.org/_lib/database_v1.min.js.
 *
 * @module api/laboratory
 */

import { useAuthStore } from '@/stores/auth'
import type { Document } from '@/types'

/**
 * API response structure.
 */
interface ApiResponse<T = unknown> {
  success: boolean
  error?: string
  documents?: string[]
  document?: Document<T>
}

/**
 * Database client for a specific namespace.
 */
export class DatabaseClient {
  private baseUrl: string
  private namespace: string

  /**
   * Create a database client.
   *
   * @param baseUrl - API base URL
   * @param namespace - Namespace to use
   */
  constructor(baseUrl: string, namespace: string) {
    this.baseUrl = baseUrl
    this.namespace = namespace
    // Completed
  }

  /**
   * Get authentication token with automatic refresh.
   *
   * @returns Bearer token or null
   */
  private async getToken(): Promise<string | null> {
    const auth = useAuthStore()
    // Use getAccessToken which handles refresh if needed
    const token = await auth.getAccessToken()
    // Completed
    return token
  }

  /**
   * Build request headers with authentication.
   *
   * @returns Headers object
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }
    const token = await this.getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    // Completed
    return headers
  }

  /**
   * Build API URL for a collection/document.
   *
   * @param collection - Collection name
   * @param id - Optional document ID
   * @returns Full API URL
   */
  private buildUrl(collection: string, id?: string): string {
    let url = `${this.baseUrl}/@db/${this.namespace}/${collection}`
    if (id) {
      url += `/${id}`
    }
    // Completed
    return url
  }

  /**
   * Execute API request with error handling.
   *
   * @param url - Request URL
   * @param options - Fetch options
   * @returns API response
   */
  private async request<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(url, {
        ...options,
        headers,
      })
      const data = await response.json()
      // Completed
      return data
    } catch (error) {
      console.error('API request error:', error)
      // Completed
      return { success: false, error: String(error) }
    }
  }

  /**
   * List documents in a collection.
   *
   * @param collection - Collection name
   * @param options - Query options
   * @returns List of document IDs
   */
  async list(collection: string, options: { limit?: number; offset?: number } = {}): Promise<string[]> {
    try {
      const params = new URLSearchParams()
      if (options.limit) params.set('limit', String(options.limit))
      if (options.offset) params.set('offset', String(options.offset))

      const url = this.buildUrl(collection) + (params.toString() ? `?${params}` : '')
      const response = await this.request<unknown>(url, { method: 'GET' })
      // Completed
      return response.success && response.documents ? response.documents : []
    } catch (error) {
      console.error('Database list error:', error)
      // Completed
      return []
    }
  }

  /**
   * Read a document by ID.
   *
   * @param collection - Collection name
   * @param id - Document ID
   * @returns Document data or null
   */
  async read<T = Record<string, unknown>>(collection: string, id: string): Promise<T | null> {
    try {
      const url = this.buildUrl(collection, id)
      const response = await this.request<T>(url, { method: 'GET' })
      if (response.success && response.document?.data) {
        // Completed
        return response.document.data as T
      }
      // Completed
      return null
    } catch (error) {
      console.error('Database read error:', error)
      // Completed
      return null
    }
  }

  /**
   * Create a new document.
   *
   * @param collection - Collection name
   * @param data - Document data
   * @returns Created document or null
   */
  async create<T = Record<string, unknown>>(collection: string, data: T): Promise<Document<T> | null> {
    try {
      const url = this.buildUrl(collection)
      const response = await this.request<T>(url, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      // Completed
      return response.success && response.document ? response.document : null
    } catch (error) {
      console.error('Database create error:', error)
      // Completed
      return null
    }
  }

  /**
   * Update a document (UPSERT).
   *
   * @param collection - Collection name
   * @param id - Document ID
   * @param data - Document data
   * @returns Success status
   */
  async update<T = Record<string, unknown>>(collection: string, id: string, data: T): Promise<boolean> {
    try {
      const url = this.buildUrl(collection, id)
      const response = await this.request<T>(url, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
      // Completed
      return response.success
    } catch (error) {
      console.error('Database update error:', error)
      // Completed
      return false
    }
  }

  /**
   * Delete a document.
   *
   * @param collection - Collection name
   * @param id - Document ID
   * @returns Success status
   */
  async remove(collection: string, id: string): Promise<boolean> {
    try {
      const url = this.buildUrl(collection, id)
      const response = await this.request<unknown>(url, { method: 'DELETE' })
      // Completed
      return response.success
    } catch (error) {
      console.error('Database delete error:', error)
      // Completed
      return false
    }
  }
}

// Singleton instance
let dbInstance: DatabaseClient | null = null
let apiBaseUrl: string | null = null

/**
 * Get database client instance.
 *
 * @param baseUrl - API base URL (required on first call)
 * @param namespace - Namespace (default: 'granite')
 * @returns Database client
 */
export function useDatabase(baseUrl?: string, namespace = 'granite'): DatabaseClient {
  if (!dbInstance) {
    if (!baseUrl) {
      throw new Error('baseUrl is required on first call')
    }
    dbInstance = new DatabaseClient(baseUrl, namespace)
  }
  // Completed
  return dbInstance
}

/**
 * Initialize database with config.
 *
 * @param baseUrl - API base URL
 * @param namespace - Namespace
 */
export function initDatabase(baseUrl: string, namespace = 'granite'): void {
  apiBaseUrl = baseUrl
  dbInstance = new DatabaseClient(baseUrl, namespace)
  // Completed
}

/**
 * Get database client for a specific namespace.
 *
 * @param namespace - Namespace to use
 * @returns Database client
 */
export function getDatabaseForNamespace(namespace: string): DatabaseClient {
  if (!apiBaseUrl) {
    throw new Error('Database must be initialized first with initDatabase()')
  }
  // Completed
  return new DatabaseClient(apiBaseUrl, namespace)
}

// Completed
